import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { WalletIcon, DownloadIcon, AlertIcon, ShieldIcon, FileIcon, XIcon } from '../components/Icons';
import { HowItWorksButton, PAYROLL_HELP } from '../components/HowItWorks';
import ConfirmDialog from '../components/ConfirmDialog';

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 13; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    opts.push({ val, label });
  }
  return opts;
}

const fmt = n => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthLabel = m => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

function PayslipModal({ row, month, finalized, onClose }) {
  function print() {
    const w = window.open('', '_blank', 'width=820,height=1000');
    w.document.write(`
      <html><head><title>Payslip — ${row.name} — ${monthLabel(month)}</title>
      <style>
        @page { margin: 0; }
        body { font-family: Arial, sans-serif; margin: 0; padding: 44px 52px; color: #1e293b; font-size: 13.5px; }
        .head { display: flex; align-items: center; gap: 14px; margin-bottom: 8px; }
        .head img { height: 52px; }
        .head .co { font-weight: bold; color: #0f1b33; font-size: 14px; }
        h2.title { text-align: center; color: #1d6fb8; letter-spacing: 0.5px; margin: 18px 0 22px; font-size: 17px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 26px; }
        td, th { border: 1px solid #cbd5e1; padding: 9px 12px; text-align: left; }
        th { background: #f8fafc; width: 24%; font-weight: 600; }
        .sec { font-weight: bold; background: #f1f5f9; }
        .sig td { height: 90px; vertical-align: top; }
        .draft { color: #d97706; font-size: 11px; margin-top: 10px; }
      </style></head><body>
      <div class="head">
        <img src="${window.location.origin}/logo.webp" onerror="this.style.display='none'" />
        <div class="co">${row.sponsorship || 'Delta Hubb for Operations & Maintenance Co.'}</div>
      </div>
      <h2 class="title">OFFICIAL PAYSLIP</h2>
      <table>
        <tr><th>Company Name</th><td>${row.sponsorship || '—'}</td><th>Payroll Month</th><td>${monthLabel(month)}</td></tr>
        <tr><th>Employee Name</th><td>${row.name}</td><th>Iqama/ID No.</th><td>${row.iqamaNo || '—'}</td></tr>
        <tr><th>Job Title</th><td>${row.designation || '—'}</td><th>Department / Site</th><td>${row.project}</td></tr>
      </table>
      <table>
        <tr><td class="sec" colspan="4">Earnings</td></tr>
        <tr><th>Basic Salary</th><td>SR ${fmt(row.basicSalary)}</td><th>Overtime</th><td>SR ${fmt(row.otPay)}</td></tr>
        <tr><th>Allowances</th><td>SR ${fmt(row.allowance)}</td><th>Deductions</th><td>SR ${fmt(row.deduction + row.installment)}</td></tr>
        <tr><th>Bonus</th><td>—</td><th>Net Salary</th><td><b>SR ${fmt(row.net)}</b></td></tr>
        <tr><th>Payment Method</th><td colspan="3">Bank${row.iban ? ' — ' + row.iban : ''}</td></tr>
      </table>
      ${row.installment > 0 ? `<p style="font-size:11.5px;color:#64748b;">Deduction installment SR ${fmt(row.installment)} applied (max 500/month). Remaining balance: SR ${fmt(row.balanceAfter)}.</p>` : ''}
      <table class="sig">
        <tr><th style="width:50%;">Employee Signature</th><th>Authorized Signatory</th></tr>
        <tr><td></td><td></td></tr>
      </table>
      ${!finalized ? '<p class="draft">DRAFT — payroll for this month is not finalized.</p>' : ''}
      <script>window.print();</script>
      </body></html>`);
    w.document.close();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,27,51,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-[560px] max-w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-[#0f1b33] flex items-center justify-center">
            <FileIcon size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-slate-800">Payslip — {monthLabel(month)}</h2>
            <p className="text-xs text-slate-400">{row.name} · {row.project}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-all">
            <XIcon size={16} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-6 text-[13.5px]">
          <div className="space-y-1.5 mb-5">
            {[['Employee', `${row.name} (#${String(row.id).padStart(4,'0')})`],
              ['Designation', row.designation || '—'],
              ['Project', row.project],
              ['Sponsorship', row.sponsorship || '—'],
              ['Iqama No.', row.iqamaNo || '—'],
              ['IBAN', row.iban || '—']].map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-slate-400">{k}</span><span className="font-medium text-slate-800">{v}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 pt-4 space-y-1.5 mb-5">
            <div className="flex justify-between"><span className="text-slate-400">Basic Salary</span><span>SAR {fmt(row.basicSalary)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Allowance</span><span>SAR {fmt(row.allowance)}</span></div>
            <div className="flex justify-between font-semibold"><span>Gross</span><span>SAR {fmt(row.totalSalary)}</span></div>
            <div className="flex justify-between text-emerald-600"><span>Overtime (client-calculated)</span><span>+ SAR {fmt(row.otPay)}</span></div>
            <div className="flex justify-between text-red-500"><span>Absence ({row.unpaidDays} days)</span><span>− SAR {fmt(row.deduction)}</span></div>
            <div className="flex justify-between text-red-500"><span>Deduction installment</span><span>− SAR {fmt(row.installment)}</span></div>
            {row.installment > 0 && (
              <div className="flex justify-between text-xs text-slate-400"><span>Balance after this month</span><span>SAR {fmt(row.balanceAfter)}</span></div>
            )}
          </div>
          <div className="flex justify-between items-center bg-[#fde8e3] rounded-xl px-5 py-3.5 font-bold text-[#0f1b33]">
            <span>NET PAY</span><span className="text-lg">SAR {fmt(row.net)}</span>
          </div>
          {!finalized && (
            <p className="text-[11.5px] text-amber-600 mt-3">Draft — this month's payroll isn't finalized yet. Numbers can still change.</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-5 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-slate-400 transition-all">Close</button>
          <button onClick={print}
            className="flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-sm font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all">
            <DownloadIcon size={14} /> Print / PDF
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Payroll() {
  const { canEdit } = useAuth();
  const [month, setMonth] = useState(monthOptions()[0].val);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState('All');
  const [rows, setRows] = useState([]);
  const [finalized, setFinalized] = useState(false);
  const [staleProjects, setStaleProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payslip, setPayslip] = useState(null);
  const [acting, setActing] = useState(false);
  const [confirm, setConfirm] = useState(null); // { action: 'finalize'|'unfinalize' }
  const [confirmError, setConfirmError] = useState('');

  useEffect(() => { api.get('/projects').then(r => setProjects(['All', ...r.data])); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { month };
    if (project !== 'All') params.project = project;
    api.get('/payroll', { params })
      .then(r => { setRows(r.data.rows); setFinalized(!!r.data.finalized); setStaleProjects(r.data.staleProjects || []); })
      .finally(() => setLoading(false));
  }, [month, project]);

  useEffect(() => { load(); }, [load]);

  async function runConfirm() {
    setActing(true); setConfirmError('');
    try {
      await api.post(`/payroll/${confirm.action}`, { month });
      setConfirm(null);
      load();
    } catch (err) {
      setConfirmError(err.response?.data?.error || 'Failed');
    } finally { setActing(false); }
  }

  const totals = rows.reduce((t, r) => ({
    gross: t.gross + r.totalSalary, deduction: t.deduction + r.deduction + r.installment,
    ot: t.ot + r.otPay, net: t.net + r.net,
  }), { gross: 0, deduction: 0, ot: 0, net: 0 });

  const missingTimesheets = [...new Set(rows.filter(r => !r.hasTimesheet).map(r => r.project))];

  function exportXLSX() {
    const data = rows.map(r => ({
      ID: r.id, Name: r.name, Project: r.project, Designation: r.designation || '',
      Sponsorship: r.sponsorship || '', 'Iqama No': r.iqamaNo || '', IBAN: r.iban || '',
      Basic: r.basicSalary, Allowance: r.allowance, 'Total Salary': r.totalSalary,
      'Unpaid Days': r.unpaidDays, 'Sick Days (paid)': r.sickDays,
      'Absence Deduction': r.deduction, Installment: r.installment, 'Balance After': r.balanceAfter,
      'OT Amount': r.otPay, 'Net Pay': r.net,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Payroll ${month}`);
    XLSX.writeFile(wb, `payroll-${month}${project !== 'All' ? '-' + project : ''}.xlsx`);
  }

  function exportPDF() {
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const M = 36;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(15, 27, 51);
    doc.text(`Delta HR — Payroll ${monthLabel(month)}${project !== 'All' ? ' — ' + project : ''}`, M, 42);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    doc.text(`${finalized ? 'FINALIZED' : 'DRAFT'} · ${rows.length} employees · generated ${new Date().toLocaleString('en-GB')}`, M, 56);

    autoTable(doc, {
      startY: 68,
      margin: { left: M, right: M },
      head: [['Employee', 'Project', 'Iqama No', 'Salary', 'Unpaid', 'Absence −', 'Installment −', 'OT +', 'Net Pay']],
      body: rows.map(r => [
        `${r.name}\n${r.designation || ''}`,
        r.project,
        r.iqamaNo || '—',
        fmt(r.totalSalary),
        r.unpaidDays || 0,
        r.deduction > 0 ? `-${fmt(r.deduction)}` : '—',
        r.installment > 0 ? `-${fmt(r.installment)}` : '—',
        r.otPay > 0 ? `+${fmt(r.otPay)}` : '—',
        fmt(r.net),
      ]),
      foot: [[
        `TOTAL — ${rows.length} employees`, '', '',
        fmt(totals.gross), '',
        `-${fmt(totals.deduction)}`, '',
        `+${fmt(totals.ot)}`,
        `SAR ${fmt(totals.net)}`,
      ]],
      styles: { fontSize: 8, cellPadding: 4.5, textColor: [30, 41, 59] },
      headStyles: { fillColor: [15, 27, 51], textColor: 255, fontSize: 7.5, fontStyle: 'bold' },
      footStyles: { fillColor: [253, 232, 227], textColor: [232, 97, 74], fontStyle: 'bold', fontSize: 8.5 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        3: { halign: 'right' }, 4: { halign: 'center' },
        5: { halign: 'right', textColor: [220, 38, 38] },
        6: { halign: 'right', textColor: [220, 38, 38] },
        7: { halign: 'right', textColor: [5, 150, 105] },
        8: { halign: 'right', fontStyle: 'bold' },
      },
    });
    doc.save(`payroll-${month}${project !== 'All' ? '-' + project.replace(/\s+/g, '_') : ''}.pdf`);
  }

  function exportCSV() {
    const header = ['ID','Name','Project','Designation','Sponsorship','Iqama No','IBAN','Basic','Allowance','Total Salary','Unpaid Days','Sick Days (paid)','Absence Deduction','Installment','Balance After','OT Amount','Net Pay'];
    const lines = rows.map(r => [
      r.id, `"${r.name}"`, `"${r.project}"`, `"${r.designation || ''}"`, `"${r.sponsorship || ''}"`, `"${r.iqamaNo || ''}"`, `"${r.iban || ''}"`,
      r.basicSalary, r.allowance, r.totalSalary, r.unpaidDays, r.sickDays, r.deduction, r.installment, r.balanceAfter, r.otPay, r.net,
    ].join(','));
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `payroll-${month}${project !== 'All' ? '-' + project : ''}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const selCls = "px-3.5 py-2.5 border border-slate-200 rounded-xl text-[13px] bg-white text-slate-600 outline-none focus:border-[#fb866d] cursor-pointer transition-colors";

  return (
    <div>
      <div className="hidden lg:flex bg-white border-b border-slate-100 px-8 py-4 items-center justify-between sticky top-0 z-40">
        <h1 className="text-lg font-bold text-slate-800">Payroll</h1>
        <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-semibold text-slate-600">Admin</span>
      </div>

      <div className="p-4 lg:p-8 stagger">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0f1b33] flex items-center justify-center">
              <WalletIcon size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-[17px] font-bold text-slate-800">Payroll Run</h2>
              <p className="text-[13px] text-slate-400">salary/30 per unpaid day · OT = client-calculated SAR amount · installments max 500/month</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            {!canEdit ? (
              <span className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold border ${
                finalized ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-500'
              }`}>
                <ShieldIcon size={14} /> {finalized ? 'Finalized' : 'Not Finalized'}
              </span>
            ) : finalized ? (
              <button onClick={() => { setConfirmError(''); setConfirm({ action: 'unfinalize' }); }} disabled={acting}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700 transition-all disabled:opacity-50">
                <ShieldIcon size={14} /> Finalized — Undo
              </button>
            ) : (
              <button onClick={() => { setConfirmError(''); setConfirm({ action: 'finalize' }); }} disabled={acting || rows.length === 0}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold border border-slate-200 text-slate-600 hover:border-slate-400 transition-all disabled:opacity-50">
                <ShieldIcon size={14} /> Finalize Month
              </button>
            )}
            <HowItWorksButton title="How Payroll Works" sections={PAYROLL_HELP} />
            <button onClick={exportPDF} disabled={rows.length === 0 || !finalized}
              title={finalized ? 'Download payroll as PDF' : 'Finalize the month first'}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold border border-slate-200 text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a] transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              <FileIcon size={14} /> PDF
            </button>
            <button onClick={exportXLSX} disabled={rows.length === 0 || !finalized}
              title={finalized ? 'Download payroll as Excel' : 'Finalize the month first'}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[13px] font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <DownloadIcon size={15} /> Excel
            </button>
          </div>
        </div>

        {/* Controls + totals */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 flex items-center gap-3 flex-wrap" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
          <select className={selCls} value={month} onChange={e => setMonth(e.target.value)}>
            {monthOptions().map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
          </select>
          <select className={selCls} value={project} onChange={e => setProject(e.target.value)}>
            {projects.map(p => <option key={p}>{p}</option>)}
          </select>
          {finalized && (
            <span className="px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11.5px] font-bold">FINALIZED</span>
          )}
          <div className="flex items-center gap-4 ml-auto flex-wrap text-[12.5px]">
            <span className="text-slate-400">Gross <b className="text-slate-700">SAR {fmt(totals.gross)}</b></span>
            <span className="text-slate-400">Deductions <b className="text-red-500">−{fmt(totals.deduction)}</b></span>
            <span className="text-slate-400">OT <b className="text-emerald-600">+{fmt(totals.ot)}</b></span>
            <span className="px-3 py-1.5 rounded-full bg-[#fde8e3] text-[#e8614a] font-bold">Net SAR {fmt(totals.net)}</span>
          </div>
        </div>

        {!finalized && missingTimesheets.length > 0 && (
          <div className="flex items-start gap-2.5 px-4 py-3 mb-4 rounded-xl bg-amber-50 border border-amber-200 text-[13px] text-amber-800">
            <AlertIcon size={16} className="flex-shrink-0 mt-0.5" />
            <div><b>No timesheet saved for:</b> {missingTimesheets.join(', ')} — computed at full salary. Enter attendance first.</div>
          </div>
        )}

        {/* Stale-snapshot warning */}
        {finalized && staleProjects.length > 0 && (
          <div className="flex items-start gap-3 px-5 py-4 mb-4 rounded-2xl bg-amber-50 border border-amber-200">
            <AlertIcon size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-[13px] text-amber-800">
              <b>Timesheets changed after this month was finalized</b> — the frozen payroll below does NOT include those changes
              ({staleProjects.join(', ')}). Click <b>Finalized — Undo</b>, then <b>Finalize Month</b> again to refresh the snapshot.
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: 1100 }}>
              <thead>
                <tr className="bg-slate-50/70">
                  {['Employee','Project','Salary','Unpaid','Absence −','Installment −','OT +','Net Pay',''].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="text-center py-12 text-slate-400 text-sm">
                    <div className="inline-flex items-center gap-2.5">
                      <div className="w-4 h-4 border-2 border-slate-200 border-t-[#fb866d] rounded-full animate-spin" />
                      Computing...
                    </div>
                  </td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-16">
                    <div className="text-[15px] font-semibold text-slate-500">No active employees</div>
                  </td></tr>
                ) : rows.map(r => (
                  <tr key={r.id} className="border-b border-slate-50 odd:bg-slate-50/50 hover:bg-[#fde8e3]/40 transition-colors last:border-0">
                    <td className="px-4 py-3">
                      <div className="text-[13.5px] font-semibold text-slate-800">{r.name}</div>
                      <div className="text-[11px] text-slate-400">{r.designation || '—'}</div>
                    </td>
                    <td className="px-4 py-3 max-w-[130px] truncate text-[13px] text-slate-600">{r.project}</td>
                    <td className="px-4 py-3 text-[13px] text-slate-700">{fmt(r.totalSalary)}</td>
                    <td className="px-4 py-3">
                      {r.unpaidDays > 0
                        ? <span className="text-[13px] font-semibold text-red-500">{r.unpaidDays}d</span>
                        : <span className="text-[13px] text-slate-400">0</span>}
                      {r.sickDays > 0 && <span className="text-[11px] text-amber-600 ml-1.5">+{r.sickDays}SL</span>}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-red-500">{r.deduction > 0 ? `−${fmt(r.deduction)}` : '—'}</td>
                    <td className="px-4 py-3">
                      {r.installment > 0
                        ? <div>
                            <span className="text-[13px] text-red-500">−{fmt(r.installment)}</span>
                            <div className="text-[10.5px] text-slate-400">bal. {fmt(r.balanceAfter)}</div>
                          </div>
                        : <span className="text-[13px] text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-emerald-600">{r.otPay > 0 ? `+${fmt(r.otPay)}` : '—'}</td>
                    <td className="px-4 py-3 text-[13.5px] font-bold text-slate-800">{fmt(r.net)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setPayslip(r)} title="Payslip"
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] hover:bg-[#fde8e3] transition-all">
                        <FileIcon size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && !loading && (
                <tfoot>
                  <tr className="bg-[#fde8e3]/60 border-t-2 border-[#fb866d]">
                    <td className="px-4 py-3.5 text-[13px] font-bold text-slate-800" colSpan={2}>TOTAL — {rows.length} employees</td>
                    <td className="px-4 py-3.5 text-[13px] font-bold text-slate-800">{fmt(totals.gross)}</td>
                    <td className="px-4 py-3.5" />
                    <td className="px-4 py-3.5 text-[13px] font-bold text-red-500" colSpan={2}>−{fmt(totals.deduction)}</td>
                    <td className="px-4 py-3.5 text-[13px] font-bold text-emerald-600">+{fmt(totals.ot)}</td>
                    <td className="px-4 py-3.5 text-[14px] font-bold text-[#e8614a]">SAR {fmt(totals.net)}</td>
                    <td className="px-4 py-3.5" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {payslip && <PayslipModal row={payslip} month={month} finalized={finalized} onClose={() => setPayslip(null)} />}

      <ConfirmDialog
        open={!!confirm}
        tone={confirm?.action === 'unfinalize' ? 'neutral' : 'primary'}
        title={confirm?.action === 'unfinalize' ? `Un-finalize ${monthLabel(month)}?` : `Finalize payroll for ${monthLabel(month)}?`}
        message={confirm?.action === 'unfinalize'
          ? 'Restores deduction balances and unfreezes the month. Payroll will recompute live from timesheets again.'
          : 'This freezes all amounts and applies deduction installments to employee balances. Timesheet changes after this won\'t affect this month.'}
        confirmLabel={confirm?.action === 'unfinalize' ? 'Un-finalize' : 'Finalize Month'}
        busy={acting}
        error={confirmError}
        onConfirm={runConfirm}
        onClose={() => !acting && setConfirm(null)}
      />
    </div>
  );
}